/**
 * The legs of an UPPER corner cabinet — ONE rule, read by the layout generator
 * (`materializeCandidate.tryEmitUpperLCorner`) and by the cabinet-run engine
 * (`cabinet-runs/upperTier.planUpperCorners`).
 *
 * It has to be one. The generator hangs an upper L, the engine re-solves the
 * run and either rebuilds that L or — where its own rule refuses one — the
 * merge carries the generator's forward. While the two rules differed (the
 * generator shrank a leg to the wall's free run; the engine demanded a fixed
 * 36" reach on both), that carried L stood on legs the engine had not planned
 * around (claude-docs/2026-09-19-upper-cabinet-tier-design.md §5a).
 *
 * The rule: each leg is the standard `UPPER_CORNER_FOOTPRINT` (24"), SHRUNK to
 * the wall's clear run from the vertex when that is shorter — and every leg
 * must still show a real door leaf past the 12" corner box
 * (`MIN_CORNER_LEAF`). An L has no meaningful trim below that: shortening a
 * leg under the box stops it being a corner cabinet, so the whole unit is
 * refused and the legs take the corner over instead.
 *
 * Pure.
 */

const {
  UPPER_CABINET_DIMENSIONS,
  UPPER_CORNER_FOOTPRINT,
  MIN_CORNER_LEAF,
} = require('./constants');

/** The shortest leg an upper L may stand on: the 12" box plus a 9" leaf. */
const MIN_UPPER_CORNER_LEG = UPPER_CABINET_DIMENSIONS.depth + MIN_CORNER_LEAF;

/**
 * @param {number} freeA clear upper-band wall from the vertex along leg A, metres
 * @param {number} freeB …along leg B
 * @returns {{ alongA: number, alongB: number } | null} the legs, or null when
 *   either cannot show a door leaf
 */
function upperCornerLegs(freeA, freeB) {
  const alongA = Math.min(UPPER_CORNER_FOOTPRINT, freeA);
  const alongB = Math.min(UPPER_CORNER_FOOTPRINT, freeB);
  if (!(alongA >= MIN_UPPER_CORNER_LEG) || !(alongB >= MIN_UPPER_CORNER_LEG)) {
    return null;
  }
  return { alongA, alongB };
}

module.exports = { upperCornerLegs, MIN_UPPER_CORNER_LEG };
